import { useState } from 'react';
import { PlusIcon } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@rocco/utils/cn';
import { useConversation } from '../../context';
import type { ContactSuggestion, DirectConversation } from '../../types';
import { useMigrateMessagesPreview } from '../../hooks/use-migrate-messages-preview';
import { useUpdateConversation } from '../../hooks/use-update-conversation';
import { ChatSettingsMembersList } from './ChatSettingsMembersList';
import { AddParticipantInput } from './AddParticipantInput';
import { ChatSettingsButton } from './ChatSettingsButton';
import { InviteToGroupChatDialog } from './InviteToGroupChatDialog';

/**
 * The "Members" tab content of the chat settings.
 * @see {@link ChatSettings}
 */
export function ChatSettingsMembers({
    className,
    conversation,
    onClose,
    onGroupChatCreated,
}: {
    className?: string;
    conversation: DirectConversation;
    onClose?: () => void;
    onGroupChatCreated?: (groupChatID: number) => void;
}) {
    const [isAddingParticipant, setIsAddingParticipant] = useState(false);
    const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
    const [participantsToAdd, setParticipantsToAdd] = useState<
        ContactSuggestion[]
    >([]);
    const { professionalID } = useConversation();
    const { clearCache: clearMigrateMessagesPreviewCache } =
        useMigrateMessagesPreview({
            conversationID: 0,
            fromTimestamp: null,
        });
    const {
        isSubmitting,
        addParticipants,
        removeParticipant,
        inviteToGroupChat,
    } = useUpdateConversation({
        conversationID: Number(conversation.ID),
        professionalID,
        onAddSuccess: () => {
            setParticipantsToAdd([]);
            onClose?.();
        },
        onRemoveSuccess: onClose,
        onInviteToGroupChatSuccess: (groupChatID) => {
            clearMigrateMessagesPreviewCache();
            setIsInviteDialogOpen(false);
            onGroupChatCreated?.(groupChatID);
            onClose?.();
        },
        onInviteToGroupChatError: (message) => {
            const errorMessage =
                message === 'One or more participants are not in your contacts'
                    ? `${message} (you may have been blocked)`
                    : message;
            toast.error(errorMessage);
        },
    });

    return (
        <div className={cn('flex flex-col gap-[8px]', className)}>
            {!isAddingParticipant ? (
                <>
                    <button
                        className="flex h-[52px] w-full items-center gap-[8px] rounded-full p-[8px] hover:bg-black/10 disabled:pointer-events-none disabled:opacity-50"
                        disabled={isSubmitting}
                        onClick={() => {
                            if (conversation.IsGroupChat) {
                                setIsAddingParticipant(true);
                            } else {
                                setIsInviteDialogOpen(true);
                            }
                        }}
                    >
                        <PlusIcon
                            className="size-[36px] shrink-0 rounded-full border border-black p-[4px]"
                            strokeWidth="1.5px"
                        />
                        <span className="min-w-0 flex-1 text-left text-[14px] leading-tight font-medium wrap-break-word">
                            Add people to this conversation
                        </span>
                    </button>
                    <ChatSettingsMembersList
                        conversation={conversation}
                        isDisabled={isSubmitting}
                        onRemoveParticipant={removeParticipant}
                    />
                </>
            ) : (
                <AddParticipantInput
                    className="min-h-0 flex-1"
                    inputClassName="h-[52px]"
                    participants={conversation.Participants}
                    participantsToAdd={participantsToAdd}
                    isDisabled={isSubmitting}
                    onParticipantsChange={setParticipantsToAdd}
                />
            )}
            <div className="mt-auto flex items-center justify-end gap-[4px]">
                {isAddingParticipant && (
                    <ChatSettingsButton
                        className="hover:bg-black/10"
                        color="transparent"
                        disabled={isSubmitting}
                        onClick={() => {
                            setIsAddingParticipant(false);
                            setParticipantsToAdd([]);
                        }}
                    >
                        Back
                    </ChatSettingsButton>
                )}
                <ChatSettingsButton
                    disabled={isSubmitting}
                    isLoading={isSubmitting}
                    onClick={() => {
                        if (isAddingParticipant && participantsToAdd.length) {
                            addParticipants(participantsToAdd.map((p) => p.ID));
                        } else {
                            onClose?.();
                        }
                    }}
                >
                    Save & Close
                </ChatSettingsButton>
            </div>

            <InviteToGroupChatDialog
                open={isInviteDialogOpen}
                conversation={conversation}
                isSubmitting={isSubmitting}
                onOpenChange={setIsInviteDialogOpen}
                onConfirm={inviteToGroupChat}
            />
        </div>
    );
}
